/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * FeedbackDetailPage -- R13 web Round 1.
 *
 * Reads a single feedback row + its thread; lets the owner change
 * status, log an internal note, send a reply by email, and (for
 * `wrong_person` with a `correct_contact`) create a new contact from
 * the submitted details.
 *
 * Server-side `requireConnectAccess` gates every call.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import connectApi from '../../services/connectApi';

const SKD_CYAN_DARK = '#0e7490';
const STATUSES = ['new', 'acknowledged', 'in_progress', 'resolved'];

export default function FeedbackDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [thread, setThread] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await connectApi.getFeedbackThread(id);
      setFeedback(data.feedback || data);
      setThread(data.thread || []);
      setErr(null);
    } catch (e) {
      setErr(e.message || 'Could not load feedback.');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    setBusy(true); setErr(null);
    try { await connectApi.patchFeedbackStatus(id, status); await load(); }
    catch (e) { setErr(e.message || 'Status change failed.'); }
    finally { setBusy(false); }
  };

  // Create contact from `wrong_person` -> uses /connect/quick-capture.
  const correct = feedback && feedback.type === 'wrong_person'
    && feedback.extra && feedback.extra.correct_contact;

  const createContactFromThis = async () => {
    if (!correct) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      const created = await connectApi.quickCapture({
        full_name: correct.name || null,
        phone:     correct.phone || null,
        email:     correct.email || null,
        source:    'typed'
      });
      const newId = (created && (created.contact && (created.contact.id || created.contact._id)))
        || created && (created.id || created._id);
      if (newId) {
        setNote('Contact created. Opening…');
        navigate(`/connect/contacts/${encodeURIComponent(newId)}`);
      } else {
        setNote('Contact created.');
        await load();
      }
    } catch (e) { setErr(e.message || 'Create contact failed.'); }
    finally { setBusy(false); }
  };

  if (err && !feedback) return (
    <div style={styles.errorBox}>
      {err} <Link to="/connect/feedback" style={styles.link}>Back to inbox</Link>
    </div>
  );
  if (!feedback) return <div style={styles.muted}>Loading…</div>;

  const contactId = feedback.contact_id;

  return (
    <section>
      <div style={{ marginBottom: 10 }}>
        <Link to="/connect/feedback" style={styles.link}>&larr; Back to inbox</Link>
      </div>

      <div style={styles.topRow}>
        <div style={{ flex: 1 }}>
          <h1 style={styles.h1}>{_prettyType(feedback.type)}</h1>
          <div style={{ color: '#64748b', fontSize: 14 }}>
            {(feedback.contact && feedback.contact.full_name) || <em style={{ color: '#94a3b8' }}>(unknown contact)</em>}
            {feedback.contact && feedback.contact.organisation ? ` · ${feedback.contact.organisation}` : ''}
            {' · '}via {feedback.source}
            {' · '}{new Date(feedback.created_at).toLocaleString()}
          </div>
          {feedback.step_title && (
            <div style={{ color: '#1d4ed8', fontSize: 13, marginTop: 4 }}>
              Step: {feedback.step_title}{feedback.rating === 1 ? ' · useful' : feedback.rating === -1 ? ' · not quite' : ''}
            </div>
          )}
          {feedback.offering_name && (
            <div style={{ color: '#1d4ed8', fontSize: 13, marginTop: 4 }}>
              {feedback.offering_name}{feedback.programme_name ? ` · ${feedback.programme_name}` : ''}
            </div>
          )}
        </div>

        <div style={styles.statusCluster}>
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => changeStatus(s)}
              disabled={busy || s === feedback.status}
              style={{
                padding: '6px 12px', borderRadius: 999, fontSize: 12,
                border: '1px solid #cbd5e1',
                background: s === feedback.status ? SKD_CYAN_DARK : 'white',
                color:      s === feedback.status ? 'white' : '#334155',
                cursor: s === feedback.status ? 'default' : 'pointer',
                textTransform: 'capitalize'
              }}
            >{s.replace('_', ' ')}</button>
          ))}
        </div>
      </div>

      {err && <div style={styles.errorBox}>{err}</div>}
      {note && <div style={styles.okBox}>{note}</div>}

      {feedback.text && (
        <div style={{ ...styles.card, marginTop: 14 }}>
          <h3 style={styles.cardH}>Message</h3>
          <div style={{ whiteSpace: 'pre-wrap', color: '#334155', fontSize: 14 }}>{feedback.text}</div>
        </div>
      )}

      {correct && (
        <div style={{ ...styles.card, marginTop: 14, background: '#fffbea', borderColor: '#fde68a' }}>
          <h3 style={{ ...styles.cardH, color: '#92400e' }}>Suggested right contact</h3>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#78350f' }}>
            {correct.name  && <li>Name: {correct.name}</li>}
            {correct.email && <li>Email: {correct.email}</li>}
            {correct.phone && <li>Phone: {correct.phone}</li>}
          </ul>
          <button
            type="button"
            onClick={createContactFromThis}
            disabled={busy}
            style={{ ...styles.btnPrimary, marginTop: 10, background: '#92400e' }}
          >
            Create contact from this
          </button>
        </div>
      )}

      <ReplyAndNote feedbackId={feedback.id} contactId={contactId} onDone={load} />

      <div style={{ ...styles.card, marginTop: 14 }}>
        <h3 style={styles.cardH}>Thread</h3>
        {thread.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 14 }}>No replies or notes yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {thread.map((m) => <ThreadRow key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function ThreadRow({ m }) {
  const tone = m.direction === 'to_contact'    ? { bg: '#eff6ff', border: '#bfdbfe', label: 'Sent' }
             : m.direction === 'from_contact'  ? { bg: '#f8fafc', border: '#e2e8f0', label: 'From contact' }
             :                                   { bg: '#fffbea', border: '#fde68a', label: 'Internal note' };
  return (
    <div style={{ padding: 12, borderRadius: 10, background: tone.bg, border: `1px solid ${tone.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong style={{ fontSize: 12, color: '#64748b' }}>{tone.label}</strong>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(m.created_at).toLocaleString()}</span>
      </div>
      {m.subject && <div style={{ color: '#0f172a', fontSize: 14, fontWeight: 600, marginTop: 4 }}>{m.subject}</div>}
      <div style={{ whiteSpace: 'pre-wrap', color: '#334155', fontSize: 14, marginTop: 4 }}>{m.body_text}</div>
    </div>
  );
}

function ReplyAndNote({ feedbackId, contactId, onDone }) {
  const [mode, setMode] = useState(null); // null | 'reply' | 'note'
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [note, setNote] = useState(null);

  const sendReply = async () => {
    if (!body.trim()) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      await connectApi.replyToFeedback(feedbackId, {
        subject: subject.trim() || undefined,
        body_text: body
      });
      setBody(''); setSubject(''); setMode(null);
      setNote('Reply sent.');
      await onDone();
    } catch (e) { setErr(e.message || 'Reply failed.'); }
    finally { setBusy(false); }
  };
  const sendNote = async () => {
    if (!body.trim()) return;
    setBusy(true); setErr(null); setNote(null);
    try {
      await connectApi.noteOnFeedback(feedbackId, body);
      setBody(''); setMode(null);
      setNote('Note saved.');
      await onDone();
    } catch (e) { setErr(e.message || 'Note save failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...styles.card, marginTop: 14 }}>
      <h3 style={styles.cardH}>Act on this</h3>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <button type="button" style={styles.btnPrimary} onClick={() => setMode('reply')}>Reply by email</button>
        <button type="button" style={styles.btnMuted} onClick={() => setMode('note')}>Add internal note</button>
        {contactId && (
          <Link to={`/connect/contacts/${encodeURIComponent(contactId)}`} style={{ ...styles.btnMuted, textDecoration: 'none' }}>
            Open contact
          </Link>
        )}
      </div>

      {err  && <div style={styles.errorBox}>{err}</div>}
      {note && <div style={styles.okBox}>{note}</div>}

      {mode === 'reply' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject (optional)"
            style={styles.input}
            maxLength={240}
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Your reply -- sent as a proper email to the contact. Threaded on the feedback row."
            rows={6}
            style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={styles.btnPrimary} disabled={busy} onClick={sendReply}>
              {busy ? 'Sending…' : 'Send reply'}
            </button>
            <button type="button" style={styles.btnMuted} disabled={busy} onClick={() => setMode(null)}>Cancel</button>
          </div>
        </div>
      )}

      {mode === 'note' && (
        <div style={{ display: 'grid', gap: 8 }}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Internal note. Not sent to the contact; visible in the thread."
            rows={4}
            style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={styles.btnPrimary} disabled={busy} onClick={sendNote}>
              {busy ? 'Saving…' : 'Save note'}
            </button>
            <button type="button" style={styles.btnMuted} disabled={busy} onClick={() => setMode(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function _prettyType(t) {
  const map = {
    goals: 'Goals they stated',
    improve: 'What would be more useful',
    request_call: 'Requested a call',
    send_more_details: 'Asked for more details',
    wrong_person: 'Not the right person',
    not_interested: 'Not interested',
    pilot_interest: 'Pilot interest',
    step_reaction: 'Reaction to a step'
  };
  return map[t] || (t || '').replace(/_/g, ' ');
}

const styles = {
  h1: { fontSize: 22, color: '#0f172a', margin: '2px 0' },
  topRow: {
    display: 'flex', gap: 16, alignItems: 'flex-start',
    flexWrap: 'wrap', marginTop: 4
  },
  statusCluster: {
    display: 'flex', gap: 4, flexWrap: 'wrap'
  },
  link: { color: SKD_CYAN_DARK, textDecoration: 'none', fontSize: 13 },
  card: {
    background: 'white', padding: 16, borderRadius: 10, border: '1px solid #e2e8f0'
  },
  cardH: { margin: '0 0 10px', color: '#0f172a', fontSize: 15 },
  input: {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
    fontSize: 14, fontFamily: 'inherit', background: 'white', boxSizing: 'border-box'
  },
  btnPrimary: {
    padding: '8px 16px', borderRadius: 8, background: '#0891b2', color: 'white',
    border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer'
  },
  btnMuted: {
    padding: '8px 16px', borderRadius: 8, background: 'white', color: '#334155',
    border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 500, cursor: 'pointer'
  },
  errorBox: {
    padding: 10, background: '#fef2f2', color: '#991b1b',
    borderRadius: 8, border: '1px solid #fecaca', marginTop: 10, fontSize: 13
  },
  okBox: {
    padding: 10, background: '#f0fdf4', color: '#166534',
    borderRadius: 8, border: '1px solid #bbf7d0', marginTop: 10, fontSize: 13
  },
  muted: { color: '#94a3b8', padding: 20, textAlign: 'center' }
};
